<template>
  <div class="pageRoot linkListPage collectionModule">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div class="listWorkspace" :class="{ 'listWorkspace--scrolled': controlBarScrolled }">
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <designer-list-filter-panel v-bind="filterPanelProps" v-on="filterPanelListeners" />
            </section>
            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div class="scrollControlBar" :class="{ 'scrollControlBar--scrolled': controlBarScrolled }">
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>
                <div v-if="!controlBarScrolled" class="scrollControlBar__stage scrollControlBar__stage--full">
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>
                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav @scroll-top="scrollToTableTop" @open-filter="openFilterDrawer" />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>
                <div class="scrollControlBar__page">
                  <span v-if="listTotalCount > 0">第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                  <span v-else class="scrollControlBar__emptyHint">暂无数据</span>
                  <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                  <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                </div>
              </div>
              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table ref="multipleTable" class="linkTable linkTable--hybrid" :data="tableData" style="width: 100%" row-key="id">
                  <el-table-column label="设计师名称" min-width="140" align="center" show-overflow-tooltip>
                    <template slot-scope="scope"><word-copy>{{ scope.row.name }}</word-copy></template>
                  </el-table-column>
                  <el-table-column label="头像" width="88" align="center">
                    <template slot-scope="scope">
                      <el-image v-if="scope.row.img" class="designerAvatar" :src="scope.row.img" fit="cover" />
                      <span v-else class="cellEmptyPill">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="类型" prop="type" width="100" align="center" />
                  <el-table-column label="已关联分类" min-width="180" align="center">
                    <template slot-scope="scope">
                      <div v-if="scope.row.categoryVOS && scope.row.categoryVOS.length" class="categoryCell">
                        <router-link
                          v-for="item in scope.row.categoryVOS"
                          :key="item.id"
                          :to="'/collection/classify/info/' + item.id"
                          class="categoryCell__link"
                        >
                          <el-tag size="mini" effect="plain" class="categoryTag pageTag" :class="'pageTag--tone-' + categoryTagTone(item.id)">
                            {{ item.name }}
                          </el-tag>
                        </router-link>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="认证" width="110" align="center">
                    <template slot-scope="scope">
                      <span class="statusPill" :class="scope.row.isAuth === 1 ? 'statusPill--online' : 'statusPill--offline'">
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ scope.row.isAuth === 1 ? '已认证' : '未认证' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column label="状态" width="110" align="center">
                    <template slot-scope="scope">
                      <span class="statusPill" :class="'statusPill--' + designerStatusPill(scope.row.status)">
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ designerStatusLabel(scope.row.status) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="280" align="center" class-name="actionColumn">
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button size="small" class="rowActionBtn rowActionBtn--ghost" @click.stop="copyKey(scope.row.key)">复制 key</el-button>
                        <el-button size="small" class="rowActionBtn rowActionBtn--ghost" @click.stop="goInfo(scope.row)">查看</el-button>
                        <el-button size="small" class="rowActionBtn rowActionBtn--ghost rowActionBtn--emphasis" @click.stop="goResources(scope.row)">资源列表</el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-user" /></div>
                <p class="tableEmpty__title">暂无设计师</p>
                <p class="tableEmpty__desc">调整筛选条件或新增设计师。</p>
                <el-button type="primary" size="small" v-check-role @click="$router.push('/resource/designer/create')">新增设计师</el-button>
              </div>
              <div v-else class="tableSkeletonWrap"><el-skeleton :rows="8" animated /></div>
            </section>
          </div>
        </div>
        <div v-if="filterDrawerOpen" class="filterDrawerLayer" role="dialog" aria-modal="true" aria-label="筛选与操作">
          <div class="filterDrawerPanel" :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }" :style="drawerPanelStyle">
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <button type="button" class="filterDrawerPanel__closeBtn" @click="closeFilterDrawer">收起</button>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <designer-list-filter-panel v-bind="{ ...filterPanelProps, showPanelSummary: true }" v-on="filterPanelListeners" />
            </div>
          </div>
          <div class="filterDrawerBackdrop" aria-hidden="true" @click="closeFilterDrawer" @wheel.prevent />
        </div>
      </div>
      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第 {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getDesignerList, getDesignerSelectAllType } from '../../../api/designerManage'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import { mutations } from '../../../utils/store'
import DesignerListFilterPanel from './DesignerListFilterPanel.vue'
import {
  DESIGNER_AUTH_OPTIONS,
  DESIGNER_STATUS_OPTIONS,
  categoryTagTone,
  designerStatusLabel,
  designerStatusPill,
  isFilterFilled,
} from './designerListHelpers'

export default {
  name: 'DesignerList',
  components: { DesignerListFilterPanel, ControlBarDisplay, ControlBarNav },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      name: '',
      tags: '',
      introduction: '',
      type: '',
      status: '',
      isAuth: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 10,
      total: 0,
      loading: false,
      typeOptions: [],
      statusOptions: DESIGNER_STATUS_OPTIONS,
      authOptions: DESIGNER_AUTH_OPTIONS,
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (!this.listTotalCount) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (!this.listTotalCount) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (!this.listTotalCount) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.name, this.tags, this.introduction, this.type, this.status, this.isAuth].filter(isFilterFilled).length
    },
    activeScopeFilters() {
      const chips = []
      if (isFilterFilled(this.name)) chips.push({ key: 'name', label: '名称', value: this.name })
      if (isFilterFilled(this.tags)) chips.push({ key: 'tags', label: '标签', value: this.tags })
      if (isFilterFilled(this.introduction)) chips.push({ key: 'introduction', label: '简介', value: this.introduction })
      if (isFilterFilled(this.type)) chips.push({ key: 'type', label: '类型', value: this.type })
      if (isFilterFilled(this.status)) chips.push({ key: 'status', label: '状态', value: designerStatusLabel(this.status) })
      if (isFilterFilled(this.isAuth)) chips.push({ key: 'isAuth', label: '认证', value: Number(this.isAuth) === 1 ? '已认证' : '未认证' })
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters.map((item) => `${item.label}: ${item.value}`).join(' · ')
    },
    filterPanelProps() {
      return {
        name: this.name,
        tags: this.tags,
        introduction: this.introduction,
        type: this.type,
        status: this.status,
        isAuth: this.isAuth,
        activeFilterCount: this.activeFilterCount,
        typeOptions: this.typeOptions,
        statusOptions: this.statusOptions,
        authOptions: this.authOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/resource/designer/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:name': (value) => { this.name = value },
        'update:tags': (value) => { this.tags = value },
        'update:introduction': (value) => { this.introduction = value },
        'update:type': (value) => { this.type = value },
        'update:status': (value) => { this.status = value },
        'update:isAuth': (value) => { this.isAuth = value },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) this.onLinkListDataRendered()
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) this.$nextTick(() => this.syncDrawerLayout())
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.loadTypes()
    this.getList()
  },
  methods: {
    categoryTagTone,
    designerStatusLabel,
    designerStatusPill,
    setBreadTitle: mutations.setBreadTitle,
    loadTypes() {
      getDesignerSelectAllType().then((res) => {
        if (res.success) this.typeOptions = res.data || []
      })
    },
    getList() {
      this.loading = true
      getDesignerList({
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        name: this.name,
        isAuth: this.isAuth,
        tags: this.tags,
        introduction: this.introduction,
        status: this.status,
        type: this.type,
      }).then((res) => {
        if (!res.success) return
        this.total = res.total
        this.tableData = res.data || []
      }).finally(() => {
        this.loading = false
      })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.name = ''
      this.tags = ''
      this.introduction = ''
      this.type = ''
      this.status = ''
      this.isAuth = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => this.getList())
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    copyKey(text) {
      const done = () => this.$message.success('key 已复制')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text || '').then(done)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text || ''
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      done()
    },
    goInfo(row) {
      if (row.name) this.setBreadTitle(`${row.name}-查看`)
      this.$router.push(`/resource/designer/info/${row.id}`)
    },
    goResources(row) {
      this.setBreadTitle(row.name)
      this.$router.push(`/resource/designer/resources/${row.id}?name=${encodeURIComponent(row.name || '')}`)
    },
  },
}
</script>

<style scoped>
@import './designerCategoryTag.css';

.rowActionBtn--emphasis {
  color: var(--link-accent) !important;
  font-weight: var(--link-font-semibold);
}
</style>
